import { useState } from 'react';
import { Button, Menu, UnstyledButton, Skeleton } from '@mantine/core';
import {
  IconLogout,
  IconUser,
  IconCreditCard,
  IconSettings,
  IconShield
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isRecipePath } from '../utils/routeHelpers';
import { useAuth } from '../hooks/useAuth';
import { useAppUser } from '../hooks/useAppUser';
import { SubscriptionStatusBadge } from './UI/SubscriptionStatusBadge';
import { useSubscription } from '../hooks/useSubscription';
import { afterPaint } from '../utils/afterPaint';
import { UserAvatar } from './UI/UserAvatar';

interface AuthButtonProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children?: React.ReactNode;
  fullWidth?: boolean;
  variant?: 'gradient' | 'light' | 'filled' | 'outline' | 'subtle' | 'white';
  styles?: {
    root?: React.CSSProperties;
  };
}

export const AuthButton = ({ size, children, fullWidth, variant = 'gradient', styles }: AuthButtonProps = {}) => {
  const { logout } = useAuth();
  const { user, loading } = useAppUser();
  const { subscription } = useSubscription();
  const [menuOpened, setMenuOpened] = useState(false);
  // True while a menu-item navigation is closing the dropdown: the close must
  // skip its exit transition and paint before the route changes, or Chrome's
  // predictive-back screenshot of the outgoing page keeps the open menu and
  // flashes it when the user later swipes back. See utils/afterPaint.
  const [instantMenuClose, setInstantMenuClose] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigateFromMenu = (to: string) => {
    setInstantMenuClose(true);
    setMenuOpened(false);
    afterPaint(() => {
      navigate(to);
      setInstantMenuClose(false);
    });
  };

  // Signing out on a personalized page leaves its stale content up; always
  // land on home. Navigate even if sign-out throws.
  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate('/');
    }
  };

  // Show skeleton while loading to prevent layout shift
  if (loading) {
    return <Skeleton height={38} width={38} circle />;
  }

  if (!user) {
    // Surfaces that pass their own CTA label ("Build your plan for free",
    // "Start free trial", "Join for free") are sign-up funnels, as is the
    // header button on recipe pages, which already reads "Sign up". They go
    // to /signup; the plain header "Sign in" goes to /signin. The auth page
    // reads the referring path and CTA label from router state to return the
    // user afterwards and attribute the conversion (see AuthPage).
    const signupIntent = Boolean(children) || isRecipePath(location.pathname);
    return (
      <Button
        onClick={() =>
          navigate(signupIntent ? '/signup' : '/signin', {
            state: {
              from: location.pathname + location.search,
              ctaLabel: typeof children === 'string' ? children : undefined,
            },
          })
        }
        variant={variant}
        {...(variant === 'gradient' && { gradient: { from: 'orange', to: 'red', deg: 45 } })}
        {...(size && { size })}
        {...(fullWidth && { fullWidth })}
        {...(styles && { style: styles.root })}
      >
        {children || (signupIntent ? 'Sign up' : 'Sign in')}
      </Button>
    );
  }

  return (
    <Menu
      shadow="md"
      width={200}
      opened={menuOpened}
      onChange={setMenuOpened}
      transitionProps={instantMenuClose ? { duration: 0 } : undefined}
    >
      <Menu.Target>
        <UnstyledButton>
          <UserAvatar photoURL={user.photoURL ?? undefined} displayName={user.displayName ?? undefined} size="md" />
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        {subscription?.status === 'trial' && (
          <>
            <Menu.Label style={{ fontSize: '16px', padding: '12px 16px' }} hiddenFrom="sm">
              <SubscriptionStatusBadge
                variant="minimal"
                size="md"
                iconSize={18}
                onClick={() => navigateFromMenu('/billing')}
              />
            </Menu.Label>
            <Menu.Label visibleFrom="sm">
              <SubscriptionStatusBadge
                variant="minimal"
                onClick={() => navigateFromMenu('/billing')}
              />
            </Menu.Label>
            <Menu.Divider />
          </>
        )}
        <Menu.Item
          leftSection={<IconUser size={18} />}
          onClick={() => navigateFromMenu(`/u/${user.uid}`)}
          style={{ fontSize: '16px', padding: '12px 16px' }}
          hiddenFrom="sm"
        >
          Profile
        </Menu.Item>
        <Menu.Item
          leftSection={<IconUser size="14" />}
          onClick={() => navigateFromMenu(`/u/${user.uid}`)}
          visibleFrom="sm"
        >
          Profile
        </Menu.Item>
        <Menu.Item
          leftSection={<IconSettings size={18} />}
          onClick={() => navigateFromMenu('/profile')}
          style={{ fontSize: '16px', padding: '12px 16px' }}
          hiddenFrom="sm"
        >
          Preferences
        </Menu.Item>
        <Menu.Item
          leftSection={<IconSettings size="14" />}
          onClick={() => navigateFromMenu('/profile')}
          visibleFrom="sm"
        >
          Preferences
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCreditCard size={18} />}
          onClick={() => navigateFromMenu('/billing')}
          style={{ fontSize: '16px', padding: '12px 16px' }}
          hiddenFrom="sm"
        >
          Billing
        </Menu.Item>
        <Menu.Item
          leftSection={<IconCreditCard size="14" />}
          onClick={() => navigateFromMenu('/billing')}
          visibleFrom="sm"
        >
          Billing
        </Menu.Item>
        {user?.isAdmin && (
          <>
            <Menu.Item
              leftSection={<IconShield size={18} />}
              onClick={() => navigateFromMenu('/admin')}
              style={{ fontSize: '16px', padding: '12px 16px' }}
              hiddenFrom="sm"
            >
              Admin
            </Menu.Item>
            <Menu.Item
              leftSection={<IconShield size="14" />}
              onClick={() => navigateFromMenu('/admin')}
              visibleFrom="sm"
            >
              Admin
            </Menu.Item>
          </>
        )}
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconLogout size={18} />}
          onClick={handleSignOut}
          color="red"
          style={{ fontSize: '16px', padding: '12px 16px' }}
          hiddenFrom="sm"
        >
          Sign out
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLogout size="14" />}
          onClick={handleSignOut}
          color="red"
          visibleFrom="sm"
        >
          Sign out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
